import { useEffect, useState } from 'react';

/**
 * On-screen keyboard height in CSS px (0 when closed or when the platform has
 * no visualViewport — desktop, SSR, jsdom). Drives the lift on bottom sheets so
 * a focused input isn't hidden behind the iOS keyboard, which viewport units
 * (vh/dvh/svh/lvh) do NOT reflect on WebKit. `active` should match how the host
 * is mounted (the same value passed to useScrollLock).
 */
export function useKeyboardInset(active: boolean): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    if (!active) {
      setInset(0);
      return;
    }
    const vv = typeof window !== 'undefined' ? window.visualViewport : undefined;
    if (!vv) return; // jsdom / unsupported → stays 0

    const update = () => {
      const kb = window.innerHeight - vv.height - vv.offsetTop;
      setInset(kb < 40 ? 0 : Math.round(kb)); // 40px threshold ignores accessory-bar jitter
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setInset(0);
    };
  }, [active]);

  return inset;
}
